/**
 * The legacy `numberKeypress` character filter for `input[data-type="number"]`
 * / `[data-formatter_type=decimal|integer]` — digits, one leading `-` when
 * `data-min` allows it, one `.` on decimal fields, nothing else.
 *
 * Legacy ships the same function in three places (`main.js`, `cms.main.js` and
 * `component_selector/view/component_selector.js:447`); this is the one copy.
 *
 * ★ ONE DELIBERATE DEVIATION, and it is the whole point of the filter.
 * Legacy tests `e.keyCode` inside a `keypress` handler:
 *
 *   if (e.keyCode >= 48 && e.keyCode <= 57) return;   // "digits top row"
 *   if (e.keyCode >= 96 && e.keyCode <= 105) return;  // "digits numpad"
 *
 * Those ranges are `keydown` codes. On `keypress`, `keyCode` is the CHARACTER
 * code — so the second line reads as 96–105 = `` ` ``, `a`, `b`, `c`, `d`, `e`,
 * `f`, `g`, `h`, `i`, and lowercase a–i sail straight through into a number
 * box. (`[8, 9, 37, 38, 39, 40, 46]` "backspace/tab/arrows" is the same
 * mistake: on keypress those are `%`, `&`, `'`, `(` and `.`.) Switching to
 * `e.key` implements what the ranges were meant to say. The pre-existing React
 * ports in `project-board/hooks/useGlobalInputBehaviors.ts` and
 * `cms-admin/useCmsFormControls.ts` made the same call.
 */
export function numberKeypressFilter(e: KeyboardEvent, input: HTMLInputElement): void {
  const key = e.key;
  const value = input.value;
  const cursorPos = input.selectionStart ?? 0;
  const step = Number(input.dataset.step || 1);
  const format =
    input.dataset.formatter_type === 'decimal' || step === 0.01 ? 'decimal' : 'integer';
  const min = input.dataset.min !== undefined ? Number(input.dataset.min) : -Infinity;
  const isAllSelected = input.selectionStart === 0 && input.selectionEnd === value.length;

  if (format !== 'decimal' && key === '.') {
    e.preventDefault();
    return;
  }
  // Select-all: the existing text is about to be replaced, so `.`/`-` are
  // judged against the field's own rules rather than against what is in the box.
  if (isAllSelected) {
    if (key === '.') {
      if (format !== 'decimal') e.preventDefault();
      return;
    }
    if (key === '-') {
      if (!(min < 0)) e.preventDefault();
      return;
    }
  }

  if (/^[0-9]$/.test(key)) return;
  if (key === '-') {
    if (cursorPos !== 0 || value.includes('-')) e.preventDefault();
    return;
  }
  if (key === '.') {
    if (format !== 'decimal' || value.includes('.')) e.preventDefault();
    return;
  }
  // Control keys ('Enter', 'Backspace', 'Tab', …) — most raise no keypress at
  // all, but never block one that does.
  if (key.length > 1) return;
  e.preventDefault();
}
